import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuthStore } from "../../stores/auth";
import {
  AdminTable,
  type AdminTableColumn,
  ConfirmModal,
  FilterToggle,
  PageHeader,
  StatusPill,
} from "../../components/admin/adminPrimitives";
import {
  pgIdList,
  useInternalLicenseIds,
} from "../../components/admin/isAdmin";

interface TransferRequest {
  id: string;
  license_id: string;
  current_fingerprint: string;
  reason: string | null;
  status: string;
  admin_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  license_key: string;
  school_name: string;
  account_id: string;
}

type Tab = "pending" | "reviewed";

export function TransferRequests() {
  const [requests, setRequests] = useState<TransferRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("pending");
  const [pendingApprove, setPendingApprove] = useState<TransferRequest | null>(
    null,
  );
  const [pendingDeny, setPendingDeny] = useState<TransferRequest | null>(null);
  const [denyNote, setDenyNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInternal, setShowInternal] = useState(false);
  const internalLicenseIds = useInternalLicenseIds();
  const user = useAuthStore((s) => s.user);

  const fetchRequests = useCallback(async () => {
    if (!showInternal && internalLicenseIds === null) return;

    setLoading(true);
    setError(null);
    let query = supabase
      .from("transfer_requests")
      .select(
        `id, license_id, current_fingerprint, reason, status, admin_note,
         reviewed_by, reviewed_at, created_at,
         licenses:license_id (
           license_key, account_id,
           accounts:account_id ( school_name )
         )`,
      )
      .order("created_at", { ascending: false });

    if (tab === "pending") {
      query = query.eq("status", "pending");
    } else {
      query = query.in("status", ["approved", "denied"]);
    }

    if (!showInternal && internalLicenseIds && internalLicenseIds.length > 0) {
      query = query.not("license_id", "in", pgIdList(internalLicenseIds));
    }

    const { data, error: fetchError } = await query;

    if (fetchError) {
      console.error("Failed to fetch transfer requests:", fetchError);
      setError("Failed to load transfer requests: " + fetchError.message);
      setRequests([]);
    } else {
      setRequests(
        (data ?? []).map((r: Record<string, unknown>) => {
          const licence = r.licenses as
            | {
                license_key: string;
                account_id: string;
                accounts?: { school_name: string } | null;
              }
            | null;
          return {
            id: r.id as string,
            license_id: r.license_id as string,
            current_fingerprint: r.current_fingerprint as string,
            reason: (r.reason as string | null) ?? null,
            status: r.status as string,
            admin_note: (r.admin_note as string | null) ?? null,
            reviewed_by: (r.reviewed_by as string | null) ?? null,
            reviewed_at: (r.reviewed_at as string | null) ?? null,
            created_at: r.created_at as string,
            license_key: licence?.license_key ?? "Unknown",
            school_name: licence?.accounts?.school_name ?? "Unknown",
            account_id: licence?.account_id ?? "",
          };
        }),
      );
    }
    setLoading(false);
  }, [tab, showInternal, internalLicenseIds]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  async function sendTransferEmail(requestId: string) {
    const { error: emailError } = await supabase.functions.invoke(
      "send-transfer-email",
      { body: { request_id: requestId } },
    );
    if (emailError) {
      console.error("Failed to send transfer email:", emailError);
    }
  }

  async function confirmApprove() {
    if (!pendingApprove) return;
    setBusy(true);
    try {
      const { error: deactivateError } = await supabase
        .from("activations")
        .update({ deactivated_at: new Date().toISOString() })
        .eq("license_id", pendingApprove.license_id)
        .is("deactivated_at", null);
      if (deactivateError) throw deactivateError;

      const { error: approveError } = await supabase
        .from("transfer_requests")
        .update({
          status: "approved",
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", pendingApprove.id);
      if (approveError) throw approveError;

      sendTransferEmail(pendingApprove.id);
      setPendingApprove(null);
      await fetchRequests();
    } catch (err) {
      setError(
        "Approve failed: " +
          (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setBusy(false);
    }
  }

  async function confirmDeny() {
    if (!pendingDeny) return;
    setBusy(true);
    try {
      const { error: denyError } = await supabase
        .from("transfer_requests")
        .update({
          status: "denied",
          admin_note: denyNote.trim() || null,
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", pendingDeny.id);
      if (denyError) throw denyError;

      sendTransferEmail(pendingDeny.id);
      setPendingDeny(null);
      setDenyNote("");
      await fetchRequests();
    } catch (err) {
      setError(
        "Deny failed: " + (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setBusy(false);
    }
  }

  const columns: AdminTableColumn<TransferRequest>[] = [
    {
      key: "school",
      header: "School",
      render: (r) =>
        r.account_id ? (
          <Link
            to={`/admin/users/${r.account_id}`}
            className="font-medium text-forest-900 hover:underline"
          >
            {r.school_name}
          </Link>
        ) : (
          <span className="font-medium text-forest-900">{r.school_name}</span>
        ),
    },
    {
      key: "licence",
      header: "Licence",
      render: (r) => (
        <code className="font-mono text-xs text-forest-600">
          {r.license_key}
        </code>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      render: (r) => <span className="text-forest-600">{r.reason ?? "\u2014"}</span>,
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (r) => (
        <span className="text-forest-500">
          {new Date(r.created_at).toLocaleDateString("en-GB")}
        </span>
      ),
    },
    {
      key: "action",
      header: tab === "pending" ? "Actions" : "Status",
      align: "right",
      render: (r) =>
        tab === "pending" ? (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setPendingApprove(r)}
              className="rounded-lg border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              Approve
            </button>
            <button
              onClick={() => {
                setDenyNote("");
                setPendingDeny(r);
              }}
              className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
            >
              Deny
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-1">
            <StatusPill tone={r.status === "approved" ? "emerald" : "red"}>
              {r.status}
            </StatusPill>
            {r.admin_note && (
              <p className="text-xs text-forest-400">{r.admin_note}</p>
            )}
          </div>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Transfer requests"
        description="Customers asking to move a licence to a new machine. Approve to deactivate the old fingerprint and let them re-activate; deny with a note if something looks off."
        actions={
          <FilterToggle
            label="Show internal users"
            checked={showInternal}
            onChange={setShowInternal}
          />
        }
      />

      <div className="mb-6 flex gap-2">
        {(["pending", "reviewed"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-lg px-4 py-2 text-sm font-medium capitalize transition-colors ${
              tab === t
                ? "bg-forest-900 text-cream-100"
                : "bg-white text-forest-700 ring-1 ring-forest-200 hover:bg-cream-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <AdminTable
        columns={columns}
        rows={requests}
        loading={loading}
        emptyMessage={`No ${tab} transfer requests.`}
      />

      <ConfirmModal
        open={pendingApprove !== null}
        title="Approve this transfer?"
        description={
          pendingApprove && (
            <>
              The existing activation for{" "}
              <span className="font-medium text-forest-900">
                {pendingApprove.school_name}
              </span>{" "}
              will be deactivated so the customer can re-activate on a new
              machine. A confirmation email will be sent.
            </>
          )
        }
        confirmLabel="Approve"
        confirmTone="forest"
        onConfirm={confirmApprove}
        onCancel={() => setPendingApprove(null)}
        busy={busy}
      />

      <ConfirmModal
        open={pendingDeny !== null}
        title="Deny this transfer?"
        description={
          pendingDeny && (
            <div className="space-y-3">
              <p>
                The customer at{" "}
                <span className="font-medium text-forest-900">
                  {pendingDeny.school_name}
                </span>{" "}
                will be emailed the denial reason.
              </p>
              <textarea
                value={denyNote}
                onChange={(e) => setDenyNote(e.target.value)}
                placeholder="Reason (optional, included in the denial email)"
                rows={3}
                className="paper-card w-full rounded-lg border border-forest-200 bg-white px-3 py-2 text-sm text-forest-900 focus:border-forest-500 focus:outline-none"
              />
            </div>
          )
        }
        confirmLabel="Deny transfer"
        confirmTone="red"
        onConfirm={confirmDeny}
        onCancel={() => {
          setPendingDeny(null);
          setDenyNote("");
        }}
        busy={busy}
      />
    </div>
  );
}
